import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import WhereToVoteOutlinedIcon from '@mui/icons-material/WhereToVoteOutlined';
import ResponsiveOverlay from '@/components/ResponsiveOverlay/ResponsiveOverlay';
import { useIsMobileViewport } from '@/hooks/useIsMobileViewport';

export type VisitRating = 'not_for_me' | 'liked' | 'loved';

export interface VisitStatus {
  rating: VisitRating | null;
  note: string;
}

interface Props {
  value: VisitStatus;
  onChange: (value: VisitStatus) => void;
  /** Lets the host modal ignore Escape / backdrop while the popover is open. */
  onOpenChange?: (open: boolean) => void;
  /** Mobile edit screen: smaller "Been here?" pill (13px, gray). */
  compact?: boolean;
  /** Place modal toolbar: outlined "I've been here" button (Figma Place Page). */
  variant?: 'pill' | 'toolbar';
}

const OPTIONS: { rating: VisitRating; label: string; notePlaceholder: string }[] = [
  { rating: 'not_for_me', label: 'Not for me', notePlaceholder: 'What didn’t work?' },
  { rating: 'liked', label: 'Liked it', notePlaceholder: 'What did you like?' },
  { rating: 'loved', label: 'Loved it', notePlaceholder: 'What made it great?' },
];

const POPOVER_WIDTH = 260;
const POPOVER_GAP = 8;
const VIEWPORT_PADDING = 8;
const HEART_RED = '#E5484D';

/** Figma "Been here / Owner edit mode": pill, "How was it?" popover (web) or sheet (mobile), visited pill. */
const tw = {
  pill: 'inline-flex h-9 shrink-0 items-center gap-2 rounded-full border-none bg-[#F6F5F3] px-4 font-manrope text-sm leading-5 tracking-[0.5px] text-[#525257] cursor-pointer hover:bg-[#EEECE9]',
  pillCompact: 'inline-flex h-9 shrink-0 items-center gap-1 rounded-full border-none bg-[#F6F5F3] px-3 font-manrope text-[13px] leading-[18px] text-[#6B6B6B] cursor-pointer',
  visitedPill: 'inline-flex h-9 shrink-0 items-center gap-2 rounded-full border-none bg-[#252528] py-1 pl-1 pr-4 font-manrope text-sm leading-5 tracking-[0.5px] text-white cursor-pointer hover:bg-[#3a3a3e]',
  visitedIcon: 'flex h-7 w-7 items-center justify-center rounded-full bg-white text-[#252528]',
  /** Mobile: Figma "Been here" action, 36px tall, 12px label. */
  toolbarBtn: 'inline-flex h-9 shrink-0 items-center gap-1.5 rounded-[20px] border border-solid border-[#D9D9D9] bg-white px-4 font-manrope text-sm leading-5 text-black cursor-pointer hover:bg-[#F7F7F7] max-md:h-9 max-md:gap-1 max-md:rounded-full max-md:border-[#E1E1E7] max-md:px-2.5 max-md:text-xs max-md:leading-4 max-md:tracking-[0.02em]',
  // Above the itinerary page's forced `body > .modal-overlay` z-index (999999).
  popover: 'fixed z-[1000010] flex flex-col gap-4 rounded-2xl bg-white p-4 font-manrope shadow-[0px_0px_20px_rgba(0,0,0,0.15)]',
  title: 'm-0 text-center font-manrope text-base font-semibold leading-5 tracking-[0.5px] text-[#252528]',
  options: 'flex gap-1.5',
  option: 'flex min-w-0 flex-1 flex-col items-center gap-[5px] rounded-xl border-none bg-transparent p-0 py-1 cursor-pointer hover:bg-[#F7F7F7]',
  /** Color only in the state classes: two text colors on one element resolve by CSS order, not class order */
  target: 'flex h-11 w-11 items-center justify-center rounded-full',
  targetIdle: 'text-[#292929]',
  targetSelected: 'bg-[#252528] text-white',
  optionLabel: 'font-manrope text-[11px] leading-[15px]',
  optionLabelIdle: 'text-[#4D4D4D]',
  optionLabelSelected: 'font-semibold text-[#252528]',
  addWhy: 'self-center border-none bg-transparent p-0 font-manrope text-xs leading-4 tracking-[0.02em] text-[#525257] cursor-pointer hover:text-[#252528]',
  note: 'w-full box-border resize-none rounded-xl border-none bg-[#F2F2F2] px-4 py-3 font-manrope text-sm leading-5 tracking-[0.02em] text-[#252528] outline-none placeholder:text-[#7A7A80] focus:ring-1 focus:ring-[#A7A7AE]',
  doneBtn: 'flex h-[52px] w-full items-center justify-center rounded-full border-none bg-[#252528] font-manrope text-base font-medium leading-5 text-white cursor-pointer hover:bg-[#3a3a3e]',
};

/** Figma "Mobile · Been here quick rating / add a note": bottom sheet */
const sheet = {
  /**
   * The shared sheet shell with 20px more room under the last row. Spelled out instead of appending a
   * second `!pb-*`: two paddings on one element resolve by CSS order, not class order.
   */
  panel: '!flex-none flex w-full max-h-[90dvh] box-border flex-col gap-5 !overflow-y-auto rounded-t-[24px] bg-white px-5 pt-2 !pb-[calc(40px+env(safe-area-inset-bottom,0px))] font-manrope',
  title: 'm-0 text-center font-manrope text-xl font-semibold leading-[22px] tracking-[0.02em] text-[#252528]',
  options: 'flex gap-2',
  option: 'flex min-w-0 flex-1 flex-col items-center gap-2 border-none bg-transparent p-0 cursor-pointer',
  target: 'flex h-14 w-14 items-center justify-center rounded-full',
  targetIdle: 'bg-[#F6F5F3] text-[#292929]',
  optionLabel: 'font-manrope text-xs leading-4',
  optionLabelIdle: 'text-black',
  note: 'w-full box-border resize-none rounded-xl border border-solid border-[#D9D9D9] bg-white px-4 py-2.5 font-manrope text-sm leading-5 tracking-[0.02em] text-[#252528] outline-none placeholder:text-[#A7A7AE] focus:border-[#A7A7AE]',
  doneBtn: 'flex h-10 w-full items-center justify-center rounded-[10px] border-none bg-[#252528] font-manrope text-[13px] leading-5 tracking-[0.5px] text-white cursor-pointer',
};

const RatingIcon: React.FC<{ rating: VisitRating; selected: boolean; size: number }> = ({ rating, selected, size }) => {
  const sx = { fontSize: size };
  if (rating === 'loved') return selected ? <FavoriteIcon sx={{ ...sx, color: HEART_RED }} /> : <FavoriteBorderIcon sx={sx} />;
  if (rating === 'liked') return <ThumbUpOffAltIcon sx={sx} />;
  return <ThumbDownOffAltIcon sx={sx} />;
};

/**
 * "Been here?" for the owner: a quick rating (Not for me / Liked it / Loved it), an optional
 * "why" note, then the pill shows the rating.
 */
const BeenHereRating: React.FC<Props> = ({ value, onChange, onOpenChange, compact = false, variant = 'pill' }) => {
  const isSheet = useIsMobileViewport();
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState<VisitStatus>(value);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const pillRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const setOpen = (open: boolean) => {
    setIsOpen(open);
    onOpenChange?.(open);
    if (open) {
      setDraft(value);
      setIsAddingNote(!!value.note);
    } else {
      setPosition(null);
    }
  };

  // Below the pill, right edges lined up, inside the viewport.
  useLayoutEffect(() => {
    if (!isOpen || isSheet || !pillRef.current || !popoverRef.current) return;
    const place = () => {
      const pill = pillRef.current!.getBoundingClientRect();
      const height = popoverRef.current!.getBoundingClientRect().height;
      const left = Math.max(VIEWPORT_PADDING, Math.min(pill.right - POPOVER_WIDTH, window.innerWidth - POPOVER_WIDTH - VIEWPORT_PADDING));
      const below = pill.bottom + POPOVER_GAP;
      const top = below + height <= window.innerHeight - VIEWPORT_PADDING ? below : Math.max(VIEWPORT_PADDING, pill.top - POPOVER_GAP - height);
      setPosition({ top, left });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [isOpen, isSheet, isAddingNote]);

  // Web popover: close on a press outside or Escape (the mobile sheet's overlay handles both).
  useEffect(() => {
    if (!isOpen || isSheet) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (popoverRef.current?.contains(target) || pillRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
    // setOpen only closes over stable setters / the onOpenChange prop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isSheet]);

  const pickRating = (rating: VisitRating) => {
    // Tapping the chosen rating again clears it.
    const next = { ...draft, rating: draft.rating === rating ? null : rating };
    setDraft(next);
    // Quick rating: without the note step, the pick is the answer.
    if (!isAddingNote) {
      onChange({ ...next, note: next.rating ? next.note : '' });
      setOpen(false);
    }
  };

  const selected = OPTIONS.find((option) => option.rating === value.rating);
  const draftOption = OPTIONS.find((option) => option.rating === draft.rating);

  const content = (
    <>
      <h3 className={isSheet ? sheet.title : tw.title}>How was it?</h3>
      <div className={isSheet ? sheet.options : tw.options} role="radiogroup" aria-label="How was it?">
        {OPTIONS.map((option) => {
          const isSelected = draft.rating === option.rating;
          return (
            <button
              key={option.rating}
              type="button"
              role="radio"
              aria-checked={isSelected}
              className={isSheet ? sheet.option : tw.option}
              onClick={() => pickRating(option.rating)}
            >
              <span className={`${isSheet ? sheet.target : tw.target} ${isSelected ? tw.targetSelected : isSheet ? sheet.targetIdle : tw.targetIdle}`}>
                <RatingIcon rating={option.rating} selected={isSelected} size={26} />
              </span>
              <span className={`${isSheet ? sheet.optionLabel : tw.optionLabel} ${isSelected ? tw.optionLabelSelected : isSheet ? sheet.optionLabelIdle : tw.optionLabelIdle}`}>
                {option.label}
              </span>
            </button>
          );
        })}
      </div>
      {isAddingNote ? (
        <>
          <textarea
            value={draft.note}
            onChange={(e) => setDraft({ ...draft, note: e.target.value })}
            className={isSheet ? sheet.note : tw.note}
            placeholder={draftOption?.notePlaceholder ?? 'Why? (optional)'}
            rows={isSheet ? 1 : 2}
            aria-label="Why (optional)"
            autoFocus
          />
          <button
            type="button"
            className={isSheet ? sheet.doneBtn : tw.doneBtn}
            onClick={() => {
              onChange({ rating: draft.rating, note: draft.rating ? draft.note.trim() : '' });
              setOpen(false);
            }}
          >
            Done
          </button>
        </>
      ) : (
        <button type="button" className={tw.addWhy} onClick={() => setIsAddingNote(true)}>
          Add why (optional)
        </button>
      )}
    </>
  );

  return (
    <>
      {variant === 'toolbar' ? (
        <button
          ref={pillRef}
          type="button"
          className={tw.toolbarBtn}
          onClick={() => setOpen(!isOpen)}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
        >
          {selected ? (
            <RatingIcon rating={selected.rating} selected size={18} />
          ) : (
            <WhereToVoteOutlinedIcon sx={{ fontSize: 20 }} aria-hidden />
          )}
          {selected ? selected.label : 'I’ve been here'}
        </button>
      ) : selected ? (
        <button
          ref={pillRef}
          type="button"
          className={tw.visitedPill}
          onClick={() => setOpen(!isOpen)}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
        >
          <span className={tw.visitedIcon} aria-hidden>
            <RatingIcon rating={selected.rating} selected size={18} />
          </span>
          {selected.label}
        </button>
      ) : (
        <button
          ref={pillRef}
          type="button"
          className={compact ? tw.pillCompact : tw.pill}
          onClick={() => setOpen(!isOpen)}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
        >
          <span aria-hidden>📍</span> Been here?
        </button>
      )}

      {isOpen && isSheet && (
        <ResponsiveOverlay onClose={() => setOpen(false)} presentation="sheet" panelClassName={sheet.panel} aria-label="How was it?">
          {content}
        </ResponsiveOverlay>
      )}

      {isOpen &&
        !isSheet &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={popoverRef}
            role="dialog"
            aria-label="How was it?"
            className={tw.popover}
            style={{ width: POPOVER_WIDTH, top: position?.top ?? 0, left: position?.left ?? 0, visibility: position ? 'visible' : 'hidden' }}
            // Portal events still bubble through the React tree to the card's drag / click listeners.
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {content}
          </div>,
          document.body,
        )}
    </>
  );
};

export default BeenHereRating;
